import styled, { css } from 'styled-components';

export const MapViewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

export const MapViewFrame = styled.div`
  position: relative;
  width: 100%;
  /* A size container so MapViewCanvas below can fit itself to it with cq units. */
  height: 100%;
  container-type: size;
  overflow: hidden;
  border-radius: ${(props): number => props.theme.kcc.borderRadius.OVERLAYS}px;
  background-color: ${(props): string => props.theme.kcc.colors.BG_LAYOUT};
`;

// The image's box: centered in MapViewFrame, sized to the image's aspect ratio (like object-fit:
// contain), so the image, SVG overlay and live marker share one coordinate space.
export const MapViewCanvas = styled.div<{ $aspectRatio?: number }>`
  position: absolute;
  ${(props): ReturnType<typeof css> =>
    props.$aspectRatio
      ? css`
          top: 50%;
          left: 50%;
          width: min(100cqw, calc(100cqh * ${props.$aspectRatio}));
          aspect-ratio: ${props.$aspectRatio};
          transform: translate(-50%, -50%);
        `
      : css`
          inset: 0;
        `}
`;

// Stretches the schedule overlay (height-capped, ratio-sized) to the canvas so trail, marker and
// zones line up with the image.
export const MapViewCleaningAreaOverlayWrapper = styled.div`
  position: absolute;
  inset: 0;

  && > div {
    height: 100%;
    max-height: none;
  }

  && > div > div {
    height: 100%;
    max-height: none;
    aspect-ratio: auto;
  }
`;

export const MapViewImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const MapViewOverlaySvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const MapViewPlannedPathPolyline = styled.polyline`
  fill: none;
  stroke: rgba(212, 212, 212, 1);
  stroke-width: 1px;
  /* Keep stroke width in screen pixels despite the stretched 0-100 viewBox. */
  vector-effect: non-scaling-stroke;
`;

export const MapViewTrailPolyline = styled.polyline`
  fill: none;
  stroke: rgba(75, 75, 75, 1);
  stroke-width: 1px;
  vector-effect: non-scaling-stroke;
`;

export const MapViewLivePositionMarker = styled.div<{ $xPercent: number; $yPercent: number }>`
  position: absolute;
  left: ${(props): number => props.$xPercent}%;
  top: ${(props): number => props.$yPercent}%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: rgba(255, 237, 0, 0.4);
  transform: translate(-50%, -50%);
`;

export const MapViewLivePositionIcon = styled.img<{ $rotationDeg: number; $sizePx: number }>`
  width: ${(props): number => props.$sizePx}px;
  height: ${(props): number => props.$sizePx}px;
  object-fit: contain;
  transform: rotate(${(props): number => props.$rotationDeg}deg);
`;

export const MapViewPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(props): string => props.theme.kcc.spacing.XS};
  color: ${(props): string => props.theme.kcc.colors.TEXT_DISABLED};
  background-color: ${(props): string => props.theme.kcc.colors.BG_LAYOUT};
`;

export const MapViewStatusBadge = styled.div<{ $isStale: boolean }>`
  position: absolute;
  z-index: 1;
  top: ${(props): string => props.theme.kcc.spacing.BASE};
  left: ${(props): string => props.theme.kcc.spacing.LG};
  display: flex;
  align-items: center;
  gap: ${(props): string => props.theme.kcc.spacing.XXS};
  padding: ${(props): string => `${props.theme.kcc.spacing.XXS} ${props.theme.kcc.spacing.XS}`};
  background-color: white;
  font-size: ${(props): number => props.theme.kcc.typography.FONT_SIZE.BASE}px;
  color: ${(props): string => (props.$isStale ? props.theme.kcc.colors.WARNING : 'rgba(22, 22, 22, 1)')};

  .map-view__status-badge-count {
    font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};

    /* The loading spinner stands in for the elapsed time, so it matches the text size. */
    [role='progressbar'] svg {
      width: 1em;
      height: 1em;
    }
  }
`;
